import React, { useEffect, useMemo, useRef, useState } from "react";
import { Columns3 } from "lucide-react";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import { GlobalButton } from "../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import { CALL_LOG_COLUMN_DEFS } from "../../constants/callLogColumns";
import { formatCallDateDisplay } from "../../utils/callLogDates";
import "../../../user-builder/styles/userBuilderTable.css";

const COLUMN_VISIBILITY_KEY = "girinestham.office.callLogs.columnVisibility";

const COLUMN_DEFS = CALL_LOG_COLUMN_DEFS;

const isRowCompleted = (log) =>
  log?.is_call_completed === 1 ||
  log?.is_call_completed === true ||
  String(log?.is_call_completed) === "1";

const STATUS_TONE_MAP = {
  resolved: "occl-status--resolved",
  pending: "occl-status--pending",
  cancelled: "occl-status--cancelled",
  completed: "occl-status--resolved",
  open: "occl-status--pending",
  "in progress": "occl-status--inprogress",
};

function loadSavedVisibility() {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

const CallLogsTable = ({
  logs,
  loading,
  currentUserId,
  onMarkFollowUpCompleted,
  onView,
  onEdit,
  paginationMeta,
  onPageChange,
  showToolbar = true,
  columnVisibilityOverride = null,
  showPaginationFooter = true,
}) => {
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadSavedVisibility();
    const initial = {};
    for (const c of COLUMN_DEFS) {
      initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    }
    return initial;
  });

  useEffect(() => {
    const onDoc = (e) => {
      if (!pickerRef.current?.contains(e.target)) setColumnPickerOpen(false);
    };
    if (columnPickerOpen) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [columnPickerOpen]);

  const visibleColumns = useMemo(() => {
    const source = columnVisibilityOverride || columnVisibility;
    return COLUMN_DEFS.filter((c) =>
      c.id === "actions" ? true : source[c.id] !== false
    );
  }, [columnVisibility, columnVisibilityOverride]);

  const headers = useMemo(
    () =>
      visibleColumns.map((c) => ({
        key: c.id,
        label: c.label,
        className: c.id === "actions" ? "ub-table__col-actions" : "",
      })),
    [visibleColumns]
  );

  const toggleColumn = (id) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const visibleCount = COLUMN_DEFS.filter((c) => next[c.id]).length;
      if (visibleCount === 0) return prev;
      try {
        localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const statusClass = (statusName) => {
    if (!statusName) return "";
    const key = statusName.toLowerCase().trim();
    return STATUS_TONE_MAP[key] || "occl-status--pending";
  };

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "#64748b" }}>
        Loading call logs...
      </div>
    );
  }

  // Render cell content based on column key
  const renderCell = (log, columnId) => {
    switch (columnId) {
      case "date":
        return log.call_date ? formatCallDateDisplay(log.call_date) : "—";
      case "time":
        return log.call_time || "—";
      case "caller_name":
        return <span style={{ fontWeight: 500 }}>{log.caller_name || "—"}</span>;
      case "phone":
        return log.contact_number || "—";
      case "call_type":
        return log.call_type === true || log.call_type === 1 || String(log.call_type) === "1"
          ? "Incoming"
          : "Outgoing";
      case "purpose":
        return log.purpose_name || log.purpose_id || "—";
      case "action":
        return log.action_taken || "—";
      case "status":
        return (
          <span className={`occl-status-tag ${statusClass(log.status_name)}`}>
            {log.status_name || "—"}
          </span>
        );
      case "handled_by":
        return log.handled_by_name || log.handled_by || "—";
      case "follow_up":
        return log.follow_up_date ? formatCallDateDisplay(log.follow_up_date) : "—";
      case "follow_up_assigned_to": {
        if (!log.follow_up_date) return "—";
        const explicitAssignee =
          log.follow_up_assigned_to != null && String(log.follow_up_assigned_to).trim() !== "";
        const name = explicitAssignee ? log.follow_up_assignee_name : log.handled_by_name;
        const role = explicitAssignee ? log.follow_up_assignee_role : log.handled_by_role;
        if (name && role) return `${name} (${role})`;
        return name || "—";
      }
      case "duration":
        return log.call_duration ? `${log.call_duration} min` : "—";
      case "remarks":
        return log.remarks || "—";
      case "actions":
        {
          // Office may edit if they created the log or are the follow-up assignee.
          const uid = Number(currentUserId);
          const createdBy =
            log.created_by != null && log.created_by !== "" ? Number(log.created_by) : null;
          const followAssignee =
            log.follow_up_assigned_to != null && log.follow_up_assigned_to !== ""
              ? Number(log.follow_up_assigned_to)
              : null;
          const isOwnEntry =
            !Number.isNaN(uid) &&
            ((createdBy != null && !Number.isNaN(createdBy) && uid === createdBy) ||
              (followAssignee != null && !Number.isNaN(followAssignee) && uid === followAssignee) ||
              (createdBy == null &&
                followAssignee == null &&
                uid === Number(log.handled_by_id ?? log.handled_by)));
          const isCompleted = isRowCompleted(log);
          return (
            <div className="ub-table-actions">
              <GlobalTableActionButton action="view" onClick={() => onView(log.id)} />
              <GlobalTableActionButton
                action="edit"
                onClick={() => onEdit(log.id)}
                disabled={!isOwnEntry}
                title={
                  isOwnEntry
                    ? "Edit this entry"
                    : "You can edit only logs you created or that are assigned to you for follow-up"
                }
              />
              {log.follow_up_date && !isCompleted ? (
                <GlobalTableActionButton
                  action="followUpComplete"
                  onClick={() => onMarkFollowUpCompleted?.(log.id)}
                />
              ) : null}
            </div>
          );
        }
      default:
        return "—";
    }
  };

  return (
    <div>
      {showToolbar ? (
        <div
          style={{
            display: "flex",
            gap: "15px",
            marginBottom: "1rem",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <div className="vt-column-picker" ref={pickerRef}>
            <GlobalButton
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setColumnPickerOpen((o) => !o)}
              aria-expanded={columnPickerOpen}
              aria-controls="call-logs-column-panel"
            >
              <Columns3 size={16} aria-hidden />
              <span style={{ marginLeft: "0.35rem" }}>Columns</span>
            </GlobalButton>
            {columnPickerOpen && (
              <div
                id="call-logs-column-panel"
                className="vt-column-picker__panel"
                role="group"
                aria-label="Choose visible columns"
              >
                <p className="vt-column-picker__title">Show columns</p>
                <ul className="vt-column-picker__list">
                  {COLUMN_DEFS.filter((c) => c.id !== "actions").map((c) => (
                    <li key={c.id}>
                      <label className="vt-column-picker__row">
                        <input
                          type="checkbox"
                          checked={!!columnVisibility[c.id]}
                          onChange={() => toggleColumn(c.id)}
                        />
                        <span>{c.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ) : null}
      <GlobalDataTable
        headers={headers}
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Call logs table"
      >
        {(!logs || logs.length === 0) ? (
          <tr>
            <td colSpan={headers.length} style={{ textAlign: "center", padding: "3rem", color: "#94a3b8" }}>
              No records available
            </td>
          </tr>
        ) : (
          logs.map((log) => (
            <tr key={log.id}>
              {visibleColumns.map((col) => (
                <td key={col.id} className={col.id === "actions" ? "ub-table__col-actions" : ""}>
                  {renderCell(log, col.id)}
                </td>
              ))}
            </tr>
          ))
        )}
      </GlobalDataTable>

      {showPaginationFooter && paginationMeta && paginationMeta.total > 0 && (
        <div className="occl-pagination">
          <div className="occl-pagination-stats">
            Showing <strong>{logs?.length ?? 0}</strong> of <strong>{paginationMeta.total}</strong>{" "}
            records (Page <strong>{paginationMeta.page}</strong> of{" "}
            <strong>
              {Math.ceil(paginationMeta.total / paginationMeta.limit) || 1}
            </strong>
            ). Max <strong>{paginationMeta.limit}</strong> rows per page.
          </div>
          <div className="occl-pagination-controls">
            <GlobalButton
              variant="ghost"
              size="sm"
              disabled={paginationMeta.page <= 1}
              onClick={() => onPageChange(paginationMeta.page - 1)}
            >
              Previous
            </GlobalButton>
            <GlobalButton
              variant="ghost"
              size="sm"
              disabled={
                paginationMeta.page >= Math.ceil(paginationMeta.total / paginationMeta.limit)
              }
              onClick={() => onPageChange(paginationMeta.page + 1)}
            >
              Next
            </GlobalButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallLogsTable;